if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
          isBeforeAtc: 'sticky-atc-bar--before-atc',
        };

        this.submitEl.addEventListener('click', this.onClick.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');

        this.desktopInfo = this.querySelector('.sticky-atc-bar__product-info').innerHTML;
        this.breakpoint = matchMedia(FoxTheme.config?.mediaQueryMobile || '(max-width: 1023.98px)');
        this.onBreakpointChange = () => {
          this.observer?.disconnect();
          window.removeEventListener('scroll',this.scheduleMobileVisibility);
          window.removeEventListener('resize',this.handleMobileResize);
          window.visualViewport?.removeEventListener('resize', this.scheduleMobileVisibility);
          window.visualViewport?.removeEventListener('scroll', this.scheduleMobileVisibility);
          cancelAnimationFrame(this.mobileVisibilityFrame);
          this.mobileVisibilityFrame = null;
          this.querySelector('.sticky-atc-bar__product-info').innerHTML = this.desktopInfo;
          this.querySelectorAll('.sticky-atc-bar__reassurance-row').forEach(el=>el.remove());
          this.classList.remove('p9-atc-docked',this.classes.isBeforeAtc,this.classes.isOpen);
          this.style.removeProperty('--p9-handoff-opacity');
          this.init();
          this.updateButton(this.productFormActions.querySelector('[name="add"]').disabled,FoxTheme.variantStrings.soldOut);
          this.dispatchEvent(new CustomEvent('palmera:sticky-layout',{bubbles:true}));
        };
        this.breakpoint.addEventListener('change',this.onBreakpointChange);
        this.init();
        this.observeMobileMenuLayer();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        this.breakpoint?.removeEventListener('change',this.onBreakpointChange);
        this.variantUnsubscribe?.();
        this.observer?.disconnect();
        this.menuLayerObserver?.disconnect();
        window.removeEventListener('scroll', this.scheduleMobileVisibility);
        window.removeEventListener('resize', this.handleMobileResize);
        window.visualViewport?.removeEventListener('resize', this.scheduleMobileVisibility);
        window.visualViewport?.removeEventListener('scroll', this.scheduleMobileVisibility);
        cancelAnimationFrame(this.mobileVisibilityFrame);
        clearTimeout(this.menuLayerCloseTimer);
        document.documentElement.classList.remove('palmera-menu-layer-active');

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      init() {
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        this.matchMainButtonOnMobile();
        this.buildMobileCard();

        const mobileQuery = FoxTheme.config?.mediaQueryMobile || '(max-width: 1023.98px)';
        if (window.matchMedia(mobileQuery).matches) {
          this.initMobileVisibility();
          this.syncWithMainProductForm();
          return;
        }

        const stickyHeight = this.offsetHeight;
        const rootMargin = `0px 0px -${stickyHeight}px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle(this.classes.isBeforeAtc, entry.boundingClientRect.top > 0);
              this.classList.toggle(this.classes.isOpen, !entry.isIntersecting);
            });
          },
          { threshold: 0.01, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      initMobileVisibility() {
        const mainSubmit = this.productFormActions.querySelector('[name="add"]');
        if (!mainSubmit) return;

        // Let CSS track the current device safe area instead of freezing it at page load.
        this.style.removeProperty('--palmera-sticky-safe-area');

        const updateVisibility = () => {
          this.mobileVisibilityFrame = null;

          const mainButtonRect = mainSubmit.getBoundingClientRect();
          const viewportBottom = window.visualViewport
            ? window.visualViewport.offsetTop + window.visualViewport.height
            : window.innerHeight;
          // Compare the real button edges, allowing for the sticky bar's bottom padding.
          // offsetTop is unaffected by the bar's exit transform.
          const stickyButtonBottom = viewportBottom - this.offsetHeight + this.submitEl.offsetTop + this.submitEl.offsetHeight;
          const alignmentGap = mainButtonRect.bottom - stickyButtonBottom;
          const isBeforeAtc = alignmentGap > 4;
          const isPastAtc = mainButtonRect.bottom <= 0;
          const handoff = Math.max(0, Math.min(1, (alignmentGap - 4) / 20));
          this.style.setProperty('--p9-handoff-opacity', handoff.toFixed(3));
          this.classList.toggle('p9-atc-docked', !isBeforeAtc && !isPastAtc);

          if (isBeforeAtc) {
            this.classList.add(this.classes.isBeforeAtc);
          } else if (isPastAtc) {
            this.classList.remove(this.classes.isBeforeAtc);
          }

          this.classList.toggle(this.classes.isOpen, isBeforeAtc || isPastAtc);
          // Retain the outgoing label while the bar fades out; update only when shown.
          if (isBeforeAtc || isPastAtc) this.syncMobileButtonLabel(isBeforeAtc, isPastAtc);
        };

        this.scheduleMobileVisibility = () => {
          if (this.mobileVisibilityFrame) return;
          this.mobileVisibilityFrame = requestAnimationFrame(updateVisibility);
        };

        this.handleMobileResize = this.scheduleMobileVisibility;

        this.classList.add(this.classes.isBeforeAtc, this.classes.isOpen);
        window.addEventListener('scroll', this.scheduleMobileVisibility, { passive: true });
        window.addEventListener('resize', this.handleMobileResize);
        window.visualViewport?.addEventListener('resize', this.scheduleMobileVisibility, { passive: true });
        window.visualViewport?.addEventListener('scroll', this.scheduleMobileVisibility, { passive: true });
        this.scheduleMobileVisibility();
      }

      syncMobileButtonLabel(isBeforeAtc, isPastAtc) {
        const buttonText = this.submitEl.querySelector('.btn__text');
        if (!buttonText) return;

        buttonText.textContent =
          !isBeforeAtc && isPastAtc
            ? (this.closest('palmera-bundle') ? 'Legg i handlekurv' : FoxTheme.variantStrings.addToCartShort)
            : (this.closest('palmera-bundle') ? 'Legg i handlekurv' : FoxTheme.variantStrings.addToCart);
      }

      observeMobileMenuLayer() {
        const menuDrawer = document.getElementById('MenuDrawer');
        if (!menuDrawer) return;

        const layerClass = 'palmera-menu-layer-active';
        const syncLayer = () => {
          clearTimeout(this.menuLayerCloseTimer);

          if (menuDrawer.hasAttribute('open')) {
            document.documentElement.classList.add(layerClass);
            return;
          }

          this.menuLayerCloseTimer = setTimeout(() => {
            document.documentElement.classList.remove(layerClass);
          }, 600);
        };

        this.menuLayerObserver = new MutationObserver(syncLayer);
        this.menuLayerObserver.observe(menuDrawer, { attributes: true, attributeFilter: ['open'] });
        syncLayer();
      }

      matchMainButtonOnMobile() {
        if (!window.matchMedia(FoxTheme.config.mediaQueryMobile).matches) return;

        const mainSubmit = this.productFormActions.querySelector('[name="add"]');
        if (!mainSubmit) return;

        this.submitEl.className = mainSubmit.className;
        this.submitEl.querySelector('.btn__text').textContent = (this.closest('palmera-bundle') ? 'Legg i handlekurv' : FoxTheme.variantStrings.addToCart);
      }

      buildMobileCard() {
        const mobileQuery = FoxTheme.config?.mediaQueryMobile || '(max-width: 1023.98px)';
        if (!window.matchMedia(mobileQuery).matches) return;

        this.querySelectorAll('.sticky-atc-bar__reassurance-row').forEach(el=>el.remove());
        const productInfo = this.querySelector('.sticky-atc-bar__product-info');
        const productTitle = this.mainProductInfo.querySelector('.product__block--title');
        if (!productInfo || !productTitle) return;

        const titleRow = document.createElement('div');
        titleRow.className = 'palmera-title-row sticky-atc-bar__title-row';

        const titleClone = productTitle.cloneNode(true);
        this.removeDuplicateIds(titleClone);
        titleRow.append(titleClone);

        const badges = this.mainProductInfo.querySelector('.product__block--badges');
        if (badges) {
          badges.querySelectorAll('.f-badge:not(.f-badge--sale)').forEach((badge) => {
            const badgeClone = badge.cloneNode(true);
            this.removeDuplicateIds(badgeClone);
            titleRow.append(badgeClone);
          });
        }

        const summaryRow = document.createElement('div');
        summaryRow.className = 'sticky-atc-bar__summary-row';
        summaryRow.append(titleRow);
        productInfo.replaceChildren(summaryRow);

        const price = this.mainProductInfo.querySelector('.product__block--price');
        if (price) {
          const priceRow = document.createElement('div');
          priceRow.className = 'palmera-price-row sticky-atc-bar__price-row';

          const priceClone = price.cloneNode(true);
          this.removeDuplicateIds(priceClone);
          priceClone.classList.add('sticky-atc-bar__price');
          priceRow.append(priceClone);

          if (badges) {
            badges.querySelectorAll('.f-badge--sale').forEach((badge) => {
              const badgeClone = badge.cloneNode(true);
              this.removeDuplicateIds(badgeClone);
              priceRow.append(badgeClone);
            });
          }

          summaryRow.append(priceRow);
        }

        const guarantee = Array.from(this.mainProductInfo.querySelectorAll('.product__block--icon-with-text')).find(
          (block) => /(?:30|60) dagers.*garanti/i.test(block.textContent)
        );
        const guaranteeItem = guarantee?.querySelector('.icon-with-text__item');

        if (guaranteeItem) {
          const reassuranceRow = document.createElement('div');
          reassuranceRow.className = 'sticky-atc-bar__reassurance-row';

          const guaranteeClone = guaranteeItem.cloneNode(true);
          this.removeDuplicateIds(guaranteeClone);
          guaranteeClone.classList.add(
            'palmera-reassurance-pill',
            'palmera-reassurance-pill__content',
            'sticky-atc-bar__guarantee-badge'
          );
          this.setPillLabel(guaranteeClone, '60 dagers fornøydgaranti');

          reassuranceRow.append(guaranteeClone);
          const buttonGroup = this.submitEl.closest('.product-form__buttons');
          if (buttonGroup) {
            buttonGroup.before(reassuranceRow);
          } else {
            productInfo.append(reassuranceRow);
          }
        }
      }

      setPillLabel(pill, label) {
        const labelElement =
          pill.querySelector('.inline-richtext') ||
          Array.from(pill.children).find((child) => !child.matches('svg, img'));

        if (labelElement) {
          labelElement.textContent = label;
        }
      }

      setPillIcon(pill, icon) {
        if (!icon) return;

        const currentIcon = Array.from(pill.children).find((child) => child.matches('svg, img'));
        if (!currentIcon) return;

        const iconClone = icon.cloneNode(true);
        this.removeDuplicateIds(iconClone);
        currentIcon.replaceWith(iconClone);
      }

      getShippingLabel() {
        const goalElement = document.querySelector('free-shipping-goal[data-minimum-amount]');
        const currencyRate = Number(window.Shopify?.currency?.rate || 1);
        const goalInCents = Number(goalElement?.dataset.minimumAmount || 0) * currencyRate * 100;
        const productPrice = this.currentVariantPrice || this.getDisplayedPrice();

        return goalInCents > 0 && productPrice >= goalInCents
          ? 'Gratis frakt'
          : 'Rask levering';
      }

      getDisplayedPrice() {
        const priceElement =
          this.mainProductInfo.querySelector('.f-price--on-sale .f-price-item--sale') ||
          this.mainProductInfo.querySelector('.f-price-item--regular');
        const priceText = priceElement?.textContent || '';
        const normalizedPrice = priceText
          .replace(/[^\d,.-]/g, '')
          .replace(/\.(?=\d{3}(?:\D|$))/g, '')
          .replace(',', '.');

        return Math.round((Number.parseFloat(normalizedPrice) || 0) * 100);
      }

      removeDuplicateIds(element) {
        if (element.id) element.removeAttribute('id');
        element.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      }

      onClick() {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          const mobileQuery = FoxTheme.config?.mediaQueryMobile || '(max-width: 1023.98px)';
          const isMobile = window.matchMedia(mobileQuery).matches;
          addButtonText.textContent =
            isMobile && this.classList.contains(this.classes.isBeforeAtc)
              ? (this.closest('palmera-bundle') ? 'Legg i handlekurv' : FoxTheme.variantStrings.addToCart)
              : (this.closest('palmera-bundle') ? 'Legg i handlekurv' : FoxTheme.variantStrings.addToCartShort);
        }
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        this.variantUnsubscribe?.();
        this.variantUnsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          this.currentVariantPrice = variant?.price || 0;
          this.buildMobileCard();
          this.updatePrice(variant);
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}


